import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { ITitle } from '@interfaces/common/common';

import { EVendorsValues, TVendorsValues } from '@enums/common';

import { columnSizes } from '@constants/table';
import { columnHeaders, MAX_USERS_COLUMN_WIDTH } from './constants';

interface IColumns {
	openEditAlarmForm: (alarm: IAlarmManagement) => void;
	vendor: TVendorsValues;
}

export const getAlarmManagementColumns = ({ vendor, openEditAlarmForm }: IColumns) => {
	return ([
		{
			header: 'alarm id',
			accessorKey: columnHeaders.alarmId?.[vendor] || columnHeaders.alarmId.LIMENET,
			isHidden: !columnHeaders.alarmId?.[vendor],
			size: columnSizes.size115,
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={EVendorsValues.oran === vendor ? value?.title : value} />;
			},
		},
		{
			header: 'alarm name',
			accessorKey: columnHeaders.alarmName?.[vendor] || columnHeaders.alarmName.LIMENET,
			isHidden: !columnHeaders.alarmName?.[vendor],
			size: columnSizes.size215,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'group',
			accessorKey: columnHeaders.group?.[vendor] || columnHeaders.group.AWTG_ORAN,
			isHidden: !columnHeaders.group?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const { title } = getValue() || {};

				return <AdaptiveTableCell text={title} />;
			},
		},
		{
			header: 'groups',
			accessorKey: columnHeaders.groups?.[vendor] || columnHeaders.groups.LIMENET,
			isHidden: !columnHeaders.groups?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const groups = getValue()?.map(({ title }: ITitle) => title).join(', ');

				return <AdaptiveTableCell text={groups} />;
			},
		},
		{
			header: 'roles',
			accessorKey: columnHeaders.roles?.[vendor] || columnHeaders.roles.LIMENET,
			isHidden: !columnHeaders.roles?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const roles: string[] | undefined = getValue()?.map(({ title }: ITitle) => title);
				const rolesTooltip = roles?.length
					? <div>{roles.map(title => <div key={title}>{title}</div>)}</div>
					: undefined;

				return (
					<AdaptiveTableCell
						text={roles?.join(', ')}
						tooltipText={rolesTooltip}
					/>
				);
			},
		},
		{
			header: 'users',
			accessorKey: columnHeaders.users?.[vendor] || columnHeaders.users.LIMENET,
			isHidden: !columnHeaders.users?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const users: string[] | undefined = getValue()?.map(({ fullName }: { fullName: string }) => fullName);
				const usersTooltip = users?.length
					? <div>{users.map((fullName, index) => <div key={`${fullName}${index}`}>{fullName}</div>)}</div>
					: undefined;

				return (
					<AdaptiveTableCell
						text={users?.join(', ')}
						tooltipText={usersTooltip}
					/>
				);
			},
		},
		{
			header: 'status',
			accessorKey: columnHeaders.status?.[vendor] || columnHeaders.status.LIMENET,
			isHidden: !columnHeaders.status?.[vendor],
			size: columnSizes.size115,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const status = getValue();
				const title = status?.title
					? status.title
					: status
						? 'Enable'
						: 'Disable';

				return (
					<AdaptiveTableCell
						maxTextWidth={MAX_USERS_COLUMN_WIDTH}
						text={title}
					/>
				);
			},
		},
		{
			header: 'severity',
			accessorKey: columnHeaders.severity?.[vendor] || columnHeaders.severity.LIMENET,
			isHidden: !columnHeaders.severity?.[vendor],
			size: columnSizes.size85,
			cell: ({ getValue }: CellContext<any, any>) => {
				const { title, color } = getValue() || {};

				return (
					<AdaptiveTableCell
						color={color}
						text={title}
					/>
				);
			},
		},
		{
			header: 'trigger condition',
			accessorKey: columnHeaders.triggerCondition?.[vendor] || columnHeaders.triggerCondition.LIMENET,
			isHidden: true,
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'notes',
			accessorKey: columnHeaders.notes?.[vendor] || columnHeaders.notes.LIMENET,
			isHidden: !columnHeaders.notes?.[vendor],
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Alarm Code',
			accessorKey: columnHeaders.alarmCode?.[vendor] || columnHeaders.alarmCode.FLEXI_DAS,
			isHidden: !columnHeaders.alarmCode?.[vendor],
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Node type',
			accessorKey: columnHeaders.nodeType?.[vendor] || columnHeaders.nodeType.FLEXI_DAS,
			isHidden: !columnHeaders.nodeType?.[vendor],
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const { name } = getValue() || {};

				return <AdaptiveTableCell text={name} />;
			},
		},
		{
			header: 'Masked',
			accessorKey: columnHeaders.masked?.[vendor] || columnHeaders.masked.FLEXI_DAS,
			isHidden: !columnHeaders.masked?.[vendor],
			size: columnSizes.size115,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const status = getValue() ? 'Enable' : 'Disable';

				return <AdaptiveTableCell text={status} />;
			},
		},
		{
			header: 'Hysteresis',
			accessorKey: columnHeaders.hysteresis?.[vendor] || columnHeaders.hysteresis.FLEXI_DAS,
			isHidden: !columnHeaders.hysteresis?.[vendor],
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Description',
			accessorKey: columnHeaders.description?.[vendor] || columnHeaders.description.FLEXI_DAS,
			isHidden: !columnHeaders.description?.[vendor],
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Applicability',
			accessorKey: columnHeaders.applicability?.[vendor] || columnHeaders.applicability.AWTG_ORAN,
			isHidden: !columnHeaders.applicability?.[vendor],
			size: columnSizes.size215,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const { title } = getValue() || {};

				return <AdaptiveTableCell text={title} />;
			},
		},
		{
			header: 'Probable Cause',
			accessorKey: columnHeaders.probableCause?.[vendor] || columnHeaders.probableCause.AWTG_ORAN,
			isHidden: !columnHeaders.probableCause?.[vendor],
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Module Name',
			accessorKey: columnHeaders.moduleName?.[vendor] || columnHeaders.moduleName.AWTG_ORAN,
			isHidden: !columnHeaders.moduleName?.[vendor],
			size: columnSizes.size335,
			cell: ({ getValue }: CellContext<any, any>) => {
				const { title } = getValue() || {};

				return <AdaptiveTableCell text={title} />;
			},
		},
		{
			header: 'actions',
			accessorKey: 'actions',
			isHidden: !columnHeaders.actions?.[vendor],
			size: columnSizes.size80,
			enableSorting: false,
			cell: ({ row }: CellContext<any, any>) => {
				const handleEdit = () => openEditAlarmForm(row?.original);

				return (
					<Actions
						editHandler={handleEdit}
						editTooltip='Edit details'
						gridNumberOfColumns={1}
						isEdit
					/>
				);
			},
		},
	]);
};
